import React, {
  ReactNode,
  useState,
  useCallback,
  useMemo,
  useRef,
  FC,
  useEffect,
} from "react";

import {
  BoldButton,
  UnderlineButton,
  HeadlineOneButton,
} from "@draft-js-plugins/buttons";
import DraftJsEditor from "@draft-js-plugins/editor";
import createInlineToolbarPlugin from "@draft-js-plugins/inline-toolbar";
import "@draft-js-plugins/inline-toolbar/lib/plugin.css";
import createLinkifyPlugin from "@draft-js-plugins/linkify";
import "draft-js/dist/Draft.css";
import "./EditorStyle.css";
import createMentionPlugin, {
  defaultSuggestionsFilter,
  MentionData,
} from "@draft-js-plugins/mention";
import * as Sentry from "@sentry/react";
import {
  AtomicBlockUtils,
  ContentBlock,
  DraftHandleValue,
  EditorState,
  Modifier,
  RawDraftContentState,
  SelectionState,
} from "draft-js";

import { useApisMembersUploadImagesCreate } from "~/hooks";

import { BlockQuoteEditor } from "~/components/organisms";

import { EditorErrorBoundary } from "./EditorErrorBoundary";
import { EditorImage } from "./EditorImage";
import { EditorLink } from "./EditorLink";

import { AttachFileType, AvatarAndNameEmployeeType } from "~/domains";

import { RequiredHiddenField } from "~/ui";

type PropsType = {
  editorState: EditorState;
  readOnly: boolean;
  onChange: (editorState: EditorState) => void;
  mentions?: AvatarAndNameEmployeeType[];
  required?: boolean;
  canFileInText: boolean;
  className?: string;
  defaultAutoFucus?: boolean;
  placeholder?: string;
  blockQuote?: {
    content: RawDraftContentState;
    employee: AvatarAndNameEmployeeType;
    files: AttachFileType[];
    createdAt: string;
  };
};

export const Editor: FC<PropsType> = ({
  readOnly,
  editorState,
  onChange,
  mentions = [],
  required = false,
  canFileInText,
  className = "",
  defaultAutoFucus = false,
  blockQuote,
  placeholder,
}: PropsType) => {
  const editor = useRef<DraftJsEditor | null>(null);
  const { mutate: uploadRequest } = useApisMembersUploadImagesCreate();
  const [mentionSuggestOpen, setMentionSuggestOpen] = useState(false);
  const [suggestions, setSuggestions] = useState<MentionData[]>(mentions);

  const linkifyPlugin = createLinkifyPlugin({
    component: ({ href, children }: { href: string; children: ReactNode }) => {
      return (
        <EditorLink href={href} readOnly={readOnly}>
          {children}
        </EditorLink>
      );
    },
  });

  const blockRendererFn = useCallback(
    (block: ContentBlock) => {
      if (block.getType() === "atomic") {
        const entityKey = block.getEntityAt(0);
        if (!entityKey) return null;
        const entity = editorState.getCurrentContent().getEntity(entityKey);
        if (!entity) return null;
        if (entity.getType() === "image") {
          const data = entity.getData() as { src: string };
          return {
            component: EditorImage,
            editable: false,
            props: {
              src: data.src,
            },
          };
        }
      }
      return null;
    },
    [editorState],
  );
  const { MentionSuggestions, mentionPlugin } = useMemo(() => {
    const mentionPlugin = createMentionPlugin({
      mentionPrefix: "@",
      supportWhitespace: true,
      entityMutability: "IMMUTABLE",
    });
    const { MentionSuggestions } = mentionPlugin;

    return { mentionPlugin, MentionSuggestions };
  }, []);

  const onOpenChange = useCallback((open: boolean) => {
    setMentionSuggestOpen(open);
  }, []);

  const onSearchChange = useCallback(
    ({ trigger, value }: { trigger: string; value: string }) => {
      setSuggestions(defaultSuggestionsFilter(value, suggestions, trigger));
    },
    [suggestions],
  );

  const insertImage = useCallback(
    (url: string) => {
      const contentState = editorState.getCurrentContent();
      const contentStateWithEntity = contentState.createEntity(
        "image",
        "IMMUTABLE",
        { src: url },
      );
      const entityKey = contentStateWithEntity.getLastCreatedEntityKey();
      const newEditorState = EditorState.set(editorState, {
        currentContent: contentStateWithEntity,
      });
      return AtomicBlockUtils.insertAtomicBlock(newEditorState, entityKey, " ");
    },
    [editorState],
  );

  // Draft.jsの既知の問題を回避: 複数ブロックにまたがる選択範囲でのIME入力
  // https://github.com/facebookarchive/draft-js/issues/1320#issuecomment-476509968
  const handleBeforeInput = useCallback((): DraftHandleValue => {
    const selection = editorState.getSelection();
    const startKey = selection.getStartKey();
    const endKey = selection.getEndKey();

    // 選択範囲が複数ブロックにまたがっている場合、範囲を削除してから入力を許可
    if (startKey !== endKey) {
      const contentState = editorState.getCurrentContent();
      const newContentState = Modifier.removeRange(
        contentState,
        selection,
        "backward",
      );
      const newEditorState = EditorState.push(
        editorState,
        newContentState,
        "remove-range",
      );
      onChange(newEditorState);
      return "handled";
    }

    return "not-handled";
  }, [editorState, onChange]);

  // Composition開始時も同様に処理
  const handleCompositionStart = useCallback(() => {
    const selection = editorState.getSelection();
    const startKey = selection.getStartKey();
    const endKey = selection.getEndKey();

    if (startKey !== endKey) {
      const contentState = editorState.getCurrentContent();
      const newContentState = Modifier.removeRange(
        contentState,
        selection,
        "backward",
      );
      const newEditorState = EditorState.push(
        editorState,
        newContentState,
        "remove-range",
      );
      onChange(newEditorState);
    }
  }, [editorState, onChange]);

  // エディタの状態をバリデーションして、壊れた状態を修正
  const handleEditorChange = useCallback(
    (newEditorState: EditorState) => {
      try {
        const selection = newEditorState.getSelection();
        const content = newEditorState.getCurrentContent();

        // 選択範囲のブロックが存在するかチェック
        const startKey = selection.getStartKey();
        const endKey = selection.getEndKey();
        const startBlock = content.getBlockForKey(startKey);
        const endBlock = content.getBlockForKey(endKey);

        // ブロックが存在しない場合、選択範囲を最初のブロックにリセット
        if (!startBlock || !endBlock) {
          const firstBlock = content.getFirstBlock();
          const safeSelection = selection.merge({
            anchorKey: firstBlock.getKey(),
            anchorOffset: 0,
            focusKey: firstBlock.getKey(),
            focusOffset: 0,
          });
          const safeEditorState = EditorState.forceSelection(
            newEditorState,
            safeSelection,
          );
          onChange(safeEditorState);
          return;
        }

        onChange(newEditorState);
      } catch (error: unknown) {
        console.error("Editor onChange error:", error);
        if (error instanceof Error) {
          Sentry.captureException(error, {
            tags: { component: "Editor", function: "handleEditorChange" },
          });
        }
        // エラーが発生した場合でも、元の状態を保持
      }
    },
    [onChange],
  );

  const [inlineToolbarPlugin, InlineToolbar] = useMemo(() => {
    const inlineToolbarPlugin = createInlineToolbarPlugin();
    return [inlineToolbarPlugin, inlineToolbarPlugin.InlineToolbar];
  }, []);

  const handleDroppedFiles = useCallback(
    (_selection: SelectionState, blobs: Blob[]): DraftHandleValue => {
      if (!blobs[0]) return "not-handled";

      uploadRequest(
        {
          body: { image: blobs[0] },
        },
        {
          onSuccess: (data) => {
            const newState = insertImage(data.imageUrl);
            onChange(newState);
            return "handled";
          },
        },
      );
      return "handled";
    },
    [insertImage, onChange, uploadRequest],
  );

  const plugins = [linkifyPlugin, mentionPlugin, inlineToolbarPlugin].flat();

  const focus = useCallback((): void => {
    editor.current?.focus();
  }, []);

  useEffect(() => {
    defaultAutoFucus && setTimeout(() => focus(), 1);
  }, [defaultAutoFucus, focus]);

  const isTextEmpty =
    editorState.getCurrentContent().getPlainText().length <= 0;
  return (
    <div
      className={`ring-0 ${isTextEmpty && required && "relative"} ${className}`}
      onClick={focus}
    >
      <EditorErrorBoundary>
        <div onCompositionStart={handleCompositionStart}>
          <DraftJsEditor
            readOnly={readOnly}
            editorState={editorState}
            onChange={handleEditorChange}
            plugins={plugins}
            blockRendererFn={blockRendererFn}
            handleDroppedFiles={canFileInText ? handleDroppedFiles : undefined}
            handleBeforeInput={handleBeforeInput}
            ref={(element) => {
              editor.current = element;
            }}
            placeholder={placeholder}
          />
        </div>
      </EditorErrorBoundary>
      {blockQuote && (
        <BlockQuoteEditor
          blockQuoteContent={blockQuote.content}
          blockQuoteEmployee={blockQuote.employee}
          blockQuoteFiles={blockQuote.files}
          blockQuoteCreatedAt={blockQuote.createdAt}
          className="mt-2"
        />
      )}
      <InlineToolbar>
        {(externalProps) => (
          <>
            <BoldButton {...externalProps} />
            <UnderlineButton {...externalProps} />
            <HeadlineOneButton {...externalProps} />
          </>
        )}
      </InlineToolbar>
      {Boolean(mentions?.length) && (
        <MentionSuggestions
          open={mentionSuggestOpen}
          onOpenChange={onOpenChange}
          suggestions={suggestions.map((mention) => ({
            ...mention,
            avatar: mention.avatarUrl as string,
          }))}
          onSearchChange={onSearchChange}
        />
      )}
      {required && (
        <RequiredHiddenField
          value={editorState.getCurrentContent().getPlainText()}
        />
      )}
    </div>
  );
};

export type EditorType = DraftJsEditor;
